import * as THREE from "three";
import CameraControls from "camera-controls";
import { SyntheticMap } from "../models/SyntheticMap";
import { BaseActor, CameraConfig } from "./threeDComponents/behaviors/BaseActor";
// import { RobotActor } from "./threeDComponents/RobotActor";
// import { TilesRenderer } from "3d-tiles-renderer";
import { Tile3DFactory } from "./threeDComponents/Tile3DFactory";
import { ParticleSystem } from "./ParticleSystem";

export enum UserActionType {
  None = 0,
  Click = 1,
  Drag = 2,
}

export interface UserAction {
  robotPose: number[];
  clickedPose: number[];
  type: UserActionType;
}




CameraControls.install({ THREE: THREE });


export default class ThreeDScene extends THREE.Scene {
  public camera: THREE.PerspectiveCamera;
  public renderer: THREE.WebGLRenderer;
  public particleSystems: ParticleSystem[] = [];
  cameraControls: CameraControls;
  clock: THREE.Clock;
  sceneWidth: number;
  sceneHeight: number;
  targetFPS = 30;
  frameInterval = 1000 / this.targetFPS;
  public mouseMoveTargets: THREE.Object3D[] = [];
  public mouseClickTargets: THREE.Object3D[] = [];
  public mouseDownTargets: THREE.Object3D[] = [];
  public mouseWheelTargets: THREE.Object3D[] = [];
  public actors: BaseActor[] = [];
  public scannableObjects: THREE.Object3D[] = [];
  // public fpvCameras: FPVCamera[] = [];
  public tile3DFactory: Tile3DFactory | null = null;




  constructor(public div: HTMLDivElement) {
    super();
    // Clean container
    while (div.firstChild) div.removeChild(div.firstChild);
    this.sceneWidth = div.clientWidth;
    this.sceneHeight = div.clientHeight;


    // Camera setup
    this.camera = new THREE.PerspectiveCamera(
      75,
      this.sceneWidth / this.sceneHeight,
      0.1,
      2000000000
    );
    // this.camera.name = "FPVCAMERA";
    this.camera.position.set(0, 0, 10); // Move the camera higher
    this.camera.updateProjectionMatrix(); // Update the camera matrix after changes

    this.camera.up.set(0, 0, 1);
    this.camera.lookAt(0, 0, 0); // Focus on the center

    // Renderer setup
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(this.sceneWidth, this.sceneHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    div.appendChild(this.renderer.domElement);

    // Camera controls
    this.cameraControls = new CameraControls(
      this.camera,
      this.renderer.domElement
    );
    this.cameraControls.maxPolarAngle = Math.PI; // so you can't look under the floor plane
    this.cameraControls.minPolarAngle = 0;
    this.cameraControls.minDistance = 1;
    this.cameraControls.maxDistance = 20000000;
    this.cameraControls.dollySpeed = 0.25;
    this.cameraControls.truckSpeed = 1.5;
    this.cameraControls.azimuthRotateSpeed = 0.15; // Adjust horizontal rotation sensitivity
    this.cameraControls.polarRotateSpeed = 0.15;   // Adjust vertical rotation sensitivity
    this.cameraControls.dollyToCursor = true;
    this.cameraControls.smoothTime = 2.5;

    // Animation clock
    this.clock = new THREE.Clock();

    // Add listeners
    this.addListeners();

    // Start rendering loop
    this.startRenderingLoop();
  }

  public getActorsWithCameras(): BaseActor[] {
    return this.actors.filter(actor => actor instanceof BaseActor && actor.getCameraConfig() !== null) as BaseActor[];
  }


  private startRenderingLoop() {
    let lastFrameTime = performance.now();

    const animate = (currentTime: number) => {
      if (currentTime - lastFrameTime < this.frameInterval) {
        requestAnimationFrame(animate);
        return;
      }

      lastFrameTime = currentTime;

      // Handle resizing for main camera
      if (this.sceneWidth !== this.div.clientWidth || this.sceneHeight !== this.div.clientHeight) {
        this.sceneWidth = this.div.clientWidth;
        this.sceneHeight = this.div.clientHeight;
        this.camera.aspect = this.sceneWidth / this.sceneHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.sceneWidth, this.sceneHeight);
      }

      // // Handle resizing for FPV cameras
      // for (const fpvCamera of this.fpvCameras) {
      //   const width = fpvCamera.div.clientWidth;
      //   const height = fpvCamera.div.clientHeight;
      //   fpvCamera.renderer.setSize(width, height);

      //   if (fpvCamera.camera instanceof THREE.PerspectiveCamera) {
      //     fpvCamera.camera.aspect = width / height;
      //     fpvCamera.camera.updateProjectionMatrix();
      //   }
      // }

      // Render
      const delta = this.clock.getDelta();
      this.cameraControls.update(delta);
      // this.components.forEach((component) => {
      //   component.updateAnimations(delta);
      // });

      // Update the tiles renderer
      this.tile3DFactory?.update();

      this.renderer.render(this, this.camera);

      // for (const fpvCamera of this.fpvCameras) {
      //   fpvCamera.renderer.render(this, fpvCamera.camera);
      // }

      for (const actor of this.actors) {
        actor.render(delta);
      }
      for (const particleSystem of this.particleSystems) {
        particleSystem.update(delta);
      }

      requestAnimationFrame(animate);

    };
    animate(performance.now());
  }

  // Dispose of the scene
  public dispose() {
    this.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.geometry.dispose();
        if (Array.isArray(object.material)) {
          object.material.forEach((material) => material.dispose());
        } else {
          object.material.dispose();
        }
      }
    });

    this.renderer.dispose();
    this.div.removeChild(this.renderer.domElement);


  }

  public onMapLoad(map: SyntheticMap) {
    const cameraPose = map.cameraPose;
    this.cameraControls.setPosition(cameraPose.x, cameraPose.y, cameraPose.z);
    this.cameraControls.setTarget(cameraPose.u, cameraPose.v, cameraPose.w);
  }

  public onMapSave(map: SyntheticMap) {
    const cameraPos = new THREE.Vector3();
    this.cameraControls.getPosition(cameraPos);
    const cameraTarget = new THREE.Vector3();
    this.cameraControls.getTarget(cameraTarget);
    map.cameraPose = {
      x: cameraPos.x,
      y: cameraPos.y,
      z: cameraPos.z,
      u: cameraTarget.x,
      v: cameraTarget.y,
      w: cameraTarget.z,
    };
  }


  public focusOnObject(model: THREE.Object3D, offset = new THREE.Vector3(0, 3, 5)) {
    if (!model) return;

    const targetPos = model.position.clone();
    // const offset = new THREE.Vector3(0, 3, 5);

    this.cameraControls.smoothTime = 1.0;
    this.cameraControls.setLookAt(
      this.camera.position.x,
      this.camera.position.y,
      this.camera.position.z,
      targetPos.x,
      targetPos.y,
      targetPos.z,
      true
    );

    this.cameraControls.setPosition(
      targetPos.x + offset.x,
      targetPos.y + offset.y,
      targetPos.z + offset.z,
      true
    );
  }

  private addListeners() {
    window.addEventListener("resize", () => {
      // console.log("Resizing");
      this.sceneWidth = this.div.clientWidth;
      this.sceneHeight = this.div.clientHeight;
      this.camera.aspect = this.sceneWidth / this.sceneHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(this.sceneWidth, this.sceneHeight);
      // Resize FPV Cameras when the window resizes
      // for (const fpvCamera of this.fpvCameras) {
      //   const width = fpvCamera.div.clientWidth;
      //   const height = fpvCamera.div.clientHeight;
      //   fpvCamera.renderer.setSize(width, height);

      //   if (fpvCamera.camera instanceof THREE.PerspectiveCamera) {
      //     fpvCamera.camera.aspect = width / height;
      //     fpvCamera.camera.updateProjectionMatrix();
      //   }
      // }

    });
  }
}
